import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EventRow {
  readonly eventId: string;
  readonly eventType: string;
  readonly status: string;
  readonly retryCount: number;
}

export function M20EventMonitoringPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [eventType, setEventType] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function load(): void {
    apiRequest<{ data: EventRow[] }>('/v1/integration/events/queue', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: EventRow[] }>('/v1/integration/events/queue', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handlePublish(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!eventType.trim()) return;
    setActionError(null);
    let payload: Record<string, unknown> = {};
    if (payloadText.trim()) {
      try {
        payload = JSON.parse(payloadText) as Record<string, unknown>;
      } catch {
        setActionError(t('m20.eventMonitoring.invalidJson'));
        return;
      }
    }
    setBusyId('publish');
    try {
      await apiRequest('/v1/integration/events/publish', {
        method: 'POST',
        token,
        body: { eventType: eventType.trim(), payload },
      });
      setEventType('');
      setPayloadText('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function act(id: string, verb: 'consume' | 'fail'): Promise<void> {
    setActionError(null);
    setBusyId(`${verb}-${id}`);
    try {
      await apiRequest(`/v1/integration/events/${id}/${verb}`, { method: 'POST', token });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m20.eventMonitoring.loading')}</p>;
  return (
    <main aria-label="event-monitoring">
      <h1>{t('m20.eventMonitoring.title')}</h1>

      <form aria-label="event-publish-form" onSubmit={handlePublish}>
        <label>
          {t('m20.eventMonitoring.eventType')}
          <input value={eventType} onChange={(e) => setEventType(e.target.value)} />
        </label>
        <label>
          {t('m20.eventMonitoring.payload')}
          <textarea value={payloadText} onChange={(e) => setPayloadText(e.target.value)} />
        </label>
        <button type="submit" disabled={busyId === 'publish'}>{t('m20.eventMonitoring.publishBtn')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="event-queue-table">
        <thead><tr><th>{t('m20.eventMonitoring.colEvent')}</th><th>{t('m20.eventMonitoring.colStatus')}</th><th>{t('m20.eventMonitoring.colRetries')}</th><th>{t('m20.eventMonitoring.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.eventId}>
              <td>{e.eventType}</td>
              <td>{e.status}</td>
              <td>{e.retryCount}</td>
              <td>
                <button
                  type="button"
                  aria-label={`consume-${e.eventId}`}
                  disabled={busyId === `consume-${e.eventId}`}
                  onClick={() => act(e.eventId, 'consume')}
                                >{t('m20.eventMonitoring.consume')}</button>
                <button
                  type="button"
                  aria-label={`fail-${e.eventId}`}
                  disabled={busyId === `fail-${e.eventId}`}
                  onClick={() => act(e.eventId, 'fail')}
                >{t('m20.eventMonitoring.fail')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
